import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import { splitEmails } from '../../invoices/utils';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../../../../shared/hooks/usePostQuery';
import { SendByType } from '../../invoices/book-and-send/types';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { ResendDialog } from '../../shared/components/ResendDialog';
import {
    ResendMultipleInvoicesEmailInformationResponse,
    ResendMultipleInvoicesEmailRequest,
} from '../../../../../../types/internal-api';
import { ResendMultipleInvoicesFormData } from '../../shared/types';

export function ResendMultipleInvoicesDialogContainer() {
    const toast = useToast();
    const location = useLocation();
    const { logNumber } = location.state as { logNumber: number };
    const navigate = useNavigate();

    const [sending, setSending] = useState<boolean>(false);

    const { loading, data: information } = usePostQuery<ResendMultipleInvoicesEmailInformationResponse>(
        `/sales/invoice/GetResendMultipleInvoicesEmailInformation`,
        logNumber,
        !logNumber
    );

    const loadingTexts = useLocalization(textIds);

    const transformedInformation = useMemo<ResendMultipleInvoicesFormData | undefined>(() => {
        if (!information) {
            return undefined;
        }

        return {
            ...information,
            messages: [],
            sendByType: SendByType.undefined,
            exceededSendingLimit: information.exceededSendingLimit,
            emailOptions: {
                ...information.emailOptions,
                recipients: information.emailOptions.recipients.join(' '),
                recipientsCc: information.emailOptions.recipientsCc.join(' '),
                recipientsBcc: information.emailOptions.recipientsBcc.join(' '),
            },
        };
    }, [information]);

    async function resend(values: ResendMultipleInvoicesFormData) {
        setSending(true);

        const resendRequestData: ResendMultipleInvoicesEmailRequest = {
            logId: logNumber,
            emailOptions: {
                ...values.emailOptions,
                recipients: splitEmails(values.emailOptions.recipients),
                recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
            },
        };
        await internalApiClient.post(`/sales/invoice/ResendMultipleInvoicesEmail`, resendRequestData);

        setSending(false);
        toast.success(t`TOAST_INVOICE_SEND_AGAIN_PLURAL_SUCCESS`);

        refreshContentFrameAfterSuccess();
        hide();
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname);
    }

    if (!logNumber) {
        return null;
    }

    return (
        <ResendDialog
            title={t(`FORM_RESEND_MULTIPLEINVOICES_TITLE`)}
            open={location.hash === '#resend-multipleinvoices'}
            information={transformedInformation}
            hideAttachmentOptions
            loading={loading || loadingTexts || sending}
            onResend={resend}
            onHide={hide}
        />
    );
}

const textIds = [
    'TOAST_INVOICE_SEND_AGAIN_PLURAL_SUCCESS',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_RESEND_ERROR_TITLE',
    'FORM_RESEND_EMAIL',
    'FORM_RESEND_BUTTON_CANCEL',
    'FORM_RESEND_BUTTON_SEND',
    'FORM_RESEND_MULTIPLEINVOICES_TITLE',
];
